
/* eslint-disable @typescript-eslint/camelcase */
import {
  directDownloadFile,
  sleep,
  formatDateNormal,
  fileNameFromUrl,
} from "@/app/infrastructures/misc/Utils";
import { ReportController } from "@/app/ui/controllers/ReportController";
import { AccountController } from "@/app/ui/controllers/AccountController";
import groupBy from "lodash/groupBy";
import { Options, Vue, prop } from "vue-class-component";
import Skeleton from "@/app/ui/components/skeleton/index.vue";
import { FlagsMedusa } from "@/feature-flags/flags-misc-medusa";
import downloadFile from "@/app/infrastructures/misc/common-library/DownloadFile";
import { CargoV2Controller } from "@/app/ui/controllers/CargoV2Controller";
import { CargoV2DetailActivityEnum } from "@/data/payload/api/CargoV2ApiRequest";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { TAB_SUCCESS_REPORT_TYPE } from "@/app/ui/views/cargo-v2/modules/constants";
import { ReportListDownloadData } from "@/domain/entities/Report";

class Props {
  customTitle = prop<string>({
    default: "",
    type: String,
  });
  isCloseable = prop<boolean>({
    default: false,
    type: Boolean,
  });
  type = prop<string>({
    default: TAB_SUCCESS_REPORT_TYPE,
    type: String,
  });
}

@Options({
  components: {
    Skeleton,
  },
  emits: ["close"],
})
export default class WidgetDownloadCargo extends Vue.with(Props) {
  get listDownloadReport() {
    return ReportController.listDownloadReport.data;
  }

  unmounted() {
    clearInterval(this.requestInInterval);
  }

  requestInInterval: any = null;

  initiateGetListDownloadReport() {
    ReportController.onGetListDownloadReport(this.type);
    this.requestInInterval = setInterval(
      () => {
        if (this.findWaitingAndToday()) {
          ReportController.onGetListDownloadReport(this.type);
        }
      },
      10000, // interval every 10 seconds
    );
  }

  findWaitingAndToday() {
    const checkWaitingAndToday = this.listDownloadReport.findIndex(
      (data: any) => {
        return (
          data.status === "waiting" &&
          new Date(data.createdAt).getDate() === new Date().getDate()
        );
      },
    );
    return checkWaitingAndToday > -1;
  }

  get isOpen() {
    return ReportController.isOpenDownloadRequest;
  }

  get isLoading() {
    return ReportController.isLoading;
  }

  onToggleMenu() {
    ReportController.setOpenDownloadRequest(!this.isOpen);
  }
  async onDownload(item: any) {
    MainAppController.showLoading();
    const files = item.url.split(",");
    try {
      for (const file of files) {
        if (file) {
          if (item.isRequireToken) {
            downloadFile(
              file,
              this.downloadFileName(item),
              `.${this.extension(item.type)}`,
              true,
              60000,
              "GET",
            );
          } else {
            directDownloadFile(file);
          }
          await sleep(500);
        }
      }
      if (item.type === "cargo-flight-awb-receipt") {
        const cargoNo = this.downloadFileName(item).split(" -").pop()?.trim();
        CargoV2Controller.getDetailCargoV2ForPrint({
          id: cargoNo || "",
          activity: CargoV2DetailActivityEnum.IS_PRINT_AWB,
          isPrint: true,
        });
        this.$emit("success-print-awb", cargoNo);
      }
    } catch (error) {
      console.error(error);
    } finally {
      MainAppController.closeLoading();
    }
  }
  numberOfFiles(item: any) {
    const files = item.url.split(",");
    return files.length ? `(${files.length})` : "";
  }

  mounted() {
    ReportController.listDownloadReport.pagination.limit = 20;
    this.initiateInfiniteScroll();
    this.initiateGetListDownloadReport();
  }

  initiateInfiniteScroll() {
    const getNewData = () => {
      ReportController.listDownloadReport.pagination.limit += 10;
      ReportController.onGetListDownloadReport(this.type);
    };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          getNewData();
        }
      });
    }, {});

    const element: any = document.querySelector("#infinite-scroll");
    observer.observe(element);
  }

  get downloadRequestList() {
    const data = ReportController.listDownloadReport.data.map((item) => {
      const date = formatDateNormal(item.createdAt, "DD MMM YYYY");

      const addition = {
        title: this.reportTitle(item),
        dateString: date,
      };
      return { ...item, ...addition };
    });
    const grouped = groupBy(data, "dateString");
    return Object.keys(grouped).map((item) => grouped[item]);
  }

  outgoingCargoFallbackName(item: ReportListDownloadData) {
    const date = formatDateNormal(item.createdAt, "YYYYMMDDHHmmss");
    return `${this.$t("cargo.widget.outgoingCargoList")} ${date}`;
  }
  reportTitle(item: ReportListDownloadData) {
    if (item.type === "cargo-flight-awb-receipt") {
      return (
        item.filename ||
        decodeURIComponent(fileNameFromUrl(item.url))
          .split(" -")
          .slice(0, 1)
          .join(" -")
      );
    }
    return this.outgoingCargoFallbackName(item);
  }
  downloadFileName(item: ReportListDownloadData) {
    const fromMeta = item.filename?.trim();
    if (fromMeta) return fromMeta;
    if (item.type === "cargo-flight-awb-receipt") {
      return this.reportTitle(item);
    }
    return this.outgoingCargoFallbackName(item);
  }

  extension(type: string) {
    if (type === "cargo-flight-awb-receipt") {
      return "pdf";
    }
    return "xlsx";
  }

  get isForeignAccount() {
    return AccountController.accountData.accountIsForeign;
  }

  get isShowRunningText() {
    return !!FlagsMedusa.information_running_text.getValue().replace(" | ", "");
  }

  onClose(e: any) {
    e.stopPropagation();
    ReportController.setOpenDownloadRequest(false);
    this.$emit("close");
  }
}
